import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, FileSignature, Check, ArrowRight } from "lucide-react";

const Done = ({ children }) => (
  <li className="flex items-start gap-1.5 text-[12px] text-slate-600">
    <Check className="mt-[3px] size-3 shrink-0 text-teal-600" />
    <span>{children}</span>
  </li>
);

/**
 * Raised the moment a partner's commercial terms change. Everything listed has already
 * happened; the one decision left is whether to put the new paperwork in front of them now.
 */
export const TermsChangePrompt = ({ kind, party, change, onOpenChange, onSent }) => {
  const [busy, setBusy] = useState(false);
  const open = !!(party && change);
  const name = party?.company_name || party?.name || "This partner";
  const from = change?.from || {};
  const to = change?.to || {};

  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/hub/agreements/${kind}/${party.id}/send?doc=agreement`);
      toast.success(`New agreement sent to ${name} to sign`);
      onSent?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The agreement couldn't be sent");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="terms-change-prompt">
        <DialogHeader>
          <div className="text-[10.5px] font-semibold uppercase tracking-[0.22em] text-[#0055FF]">Terms changed</div>
          <DialogTitle className="mt-1">Send {name} the new agreement?</DialogTitle>
          <DialogDescription>
            Their rate has moved. New cases already use it — the paperwork is the last piece.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3.5">
          <div className="flex items-center gap-3">
            <div className="min-w-0">
              <div className="text-[9.5px] font-semibold uppercase tracking-[0.16em] text-slate-400">Was</div>
              <div className="hub-num text-[15px] text-slate-500 line-through" data-testid="terms-change-from">{from.rate || "—"}</div>
            </div>
            <ArrowRight className="size-4 shrink-0 text-slate-300" />
            <div className="min-w-0">
              <div className="text-[9.5px] font-semibold uppercase tracking-[0.16em] text-[#0055FF]">Now</div>
              <div className="hub-num text-[17px] font-semibold text-slate-900" data-testid="terms-change-to">{to.rate || "—"}</div>
            </div>
          </div>
          {to.short && <div className="mt-1.5 text-[12px] text-slate-500">{to.short}</div>}
        </div>

        <ul className="space-y-1.5" data-testid="terms-change-done">
          <Done>The change is logged against them, with who made it and when.</Done>
          {change?.notified && <Done>We've emailed them the new terms in plain English.</Done>}
          {change?.sms && <Done>We've texted them too.</Done>}
          {change?.superseded
            ? <Done>Their signed agreement is superseded — the old copy is archived and a fresh version is waiting in their portal.</Done>
            : <Done>Nothing was signed at the old rate, so there's nothing to archive.</Done>}
        </ul>

        <div className="flex flex-wrap justify-end gap-2 pt-1">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="terms-change-later">
            Not now
          </Button>
          <Button type="button" onClick={send} disabled={busy}
                  className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="terms-change-send">
            {busy ? <Loader2 className="size-4 animate-spin" /> : <><FileSignature className="mr-1.5 size-3.5" /> Send the new agreement</>}
          </Button>
        </div>
        <p className="text-[11px] text-slate-400">
          Not sending it now is fine — you can email it whenever you like from the
          <span className="font-semibold text-slate-500"> agreement icon</span> on their row.
        </p>
      </DialogContent>
    </Dialog>
  );
};
